import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Fire, Star, CheckCircle, Circle, Gift, House, SignOut, Backspace, Clock } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const kidApi = () => axios.create({
  baseURL: API,
  headers: { Authorization: `Bearer ${localStorage.getItem('kid_token') || ''}` },
});

function Confetti({ amount }) {
  const pieces = Array.from({ length: 24 });
  const emojis = ['🎉', '⭐', '✨', '🎊', '💛'];
  return (
    <div className="fixed inset-0 z-[60] pointer-events-none overflow-hidden" data-testid="kid-confetti">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 bg-white rounded-full px-6 py-3 shadow-2xl text-2xl font-extrabold text-amber-500 animate-bounce">+{amount} ⭐</div>
      {pieces.map((_, i) => (
        <span key={i} className="absolute text-2xl" style={{
          left: `${(i * 4.2 + 3) % 100}%`, top: '-8%',
          animation: `kidconfetti 2.2s ease-in ${(i % 8) * 0.12}s forwards`,
        }}>{emojis[i % emojis.length]}</span>
      ))}
      <style>{`@keyframes kidconfetti { to { transform: translateY(110vh) rotate(${360}deg); opacity: 0.6; } }`}</style>
    </div>
  );
}

const TYPE_EMOJI = { school: '🏫', tuition: '📖', hobby: '🎨', sport: '⚽', other: '📌' };

const fmtGap = (mins) => {
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'}`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m ? `${h}h ${m}m` : `${h} hour${h === 1 ? '' : 's'}`;
};

function NextUpBanner({ schedule }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);
  if (!schedule || schedule.length === 0) return null;
  const mins = now.getHours() * 60 + now.getMinutes();
  const toMin = (s) => { const [h, m] = (s || '0:0').split(':').map(Number); return h * 60 + m; };
  const ongoing = schedule.find(s => toMin(s.start) <= mins && mins < toMin(s.end));
  const next = schedule.find(s => toMin(s.start) > mins);
  let emoji, text;
  if (next) {
    const gap = toMin(next.start) - mins;
    const isSchool = next.type === 'school';
    emoji = isSchool ? '🚌' : (TYPE_EMOJI[next.type] || '⏰');
    text = isSchool ? `School bus in ${fmtGap(gap)}!` : `${next.title} in ${fmtGap(gap)}!`;
  } else if (ongoing) {
    const left = toMin(ongoing.end) - mins;
    emoji = TYPE_EMOJI[ongoing.type] || '📌';
    text = `${ongoing.title} now — ${fmtGap(left)} to go`;
  } else {
    emoji = '🎉';
    text = 'All classes done for today!';
  }
  return (
    <div className="bg-white/20 backdrop-blur rounded-2xl px-4 py-3 flex items-center gap-3 animate-pulse-slow" data-testid="kid-nextup-banner">
      <span className="text-3xl">{emoji}</span>
      <p className="text-white font-bold text-base">{text}</p>
    </div>
  );
}

function PinPad({ kid, onBack, onSuccess }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const press = async (d) => {
    if (busy) return;
    const next = (pin + d).slice(0, 4);
    setPin(next);
    setError('');
    if (next.length === 4) {
      setBusy(true);
      try {
        const r = await axios.post(`${API}/kidmode/login`, { kid_id: kid.kid_id, pin: next });
        localStorage.setItem('kid_token', r.data.token);
        onSuccess(r.data.kid);
      } catch (e) {
        setError(e.response?.data?.detail || 'Wrong PIN, try again!');
        setPin('');
      }
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6" data-testid="kid-pin-pad">
      <button onClick={onBack} className="text-white/70 text-sm self-start">← Back</button>
      <div className="w-24 h-24 rounded-full flex items-center justify-center text-5xl shadow-lg" style={{ backgroundColor: kid.avatar_color }}>{kid.avatar_emoji}</div>
      <h2 className="text-2xl font-bold text-white">Hi {kid.name}! Enter your PIN</h2>
      <div className="flex gap-3">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className={`w-5 h-5 rounded-full border-2 border-white/60 ${pin.length > i ? 'bg-white' : ''}`} />
        ))}
      </div>
      {error && <p className="text-red-300 font-semibold text-sm" data-testid="pin-error">{error}</p>}
      <div className="grid grid-cols-3 gap-3">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'del'].map((d, i) => d === '' ? <div key={i} /> : (
          <button key={i} onClick={() => d === 'del' ? setPin(pin.slice(0, -1)) : press(d)} data-testid={`pin-key-${d}`}
            className="w-16 h-16 rounded-full bg-white/15 text-white text-2xl font-bold active:scale-95 transition-transform flex items-center justify-center backdrop-blur">
            {d === 'del' ? <Backspace size={24} /> : d}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function KidModePage() {
  const [profiles, setProfiles] = useState([]);
  const [selectedKid, setSelectedKid] = useState(null);
  const [loggedKid, setLoggedKid] = useState(null);
  const [day, setDay] = useState(null);
  const [rewardsData, setRewardsData] = useState(null);
  const [view, setView] = useState('home');
  const [loading, setLoading] = useState(true);
  const [celebrate, setCelebrate] = useState(0);

  const loadDay = useCallback(async () => {
    try {
      const r = await kidApi().get('/kidmode/my-day');
      setDay(r.data);
      setLoggedKid(r.data.kid);
      const bal = r.data.kid?.points_balance || 0;
      const key = `kid_last_balance_${r.data.kid?.kid_id}`;
      const prev = parseInt(localStorage.getItem(key) || '-1');
      if (prev >= 0 && bal > prev) {
        setCelebrate(bal - prev);
        setTimeout(() => setCelebrate(0), 2600);
      }
      localStorage.setItem(key, String(bal));
      return true;
    } catch (e) {
      localStorage.removeItem('kid_token');
      setLoggedKid(null);
      return false;
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const p = await axios.get(`${API}/kidmode/profiles`);
        setProfiles(p.data);
      } catch (e) { console.error(e); }
      if (localStorage.getItem('kid_token')) await loadDay();
      setLoading(false);
    })();
  }, [loadDay]);

  const loadRewards = async () => {
    try { const r = await kidApi().get('/kidmode/rewards'); setRewardsData(r.data); } catch (e) { console.error(e); }
  };

  const markDone = async (logId) => {
    try {
      const r = await kidApi().post(`/kidmode/tasks/${logId}/done`);
      toast.success(r.data.message);
      loadDay();
    } catch (e) { toast.error(e.response?.data?.detail || 'Oops!'); }
  };

  const toggleDose = async (doseId) => {
    try { await kidApi().post(`/kidmode/doses/${doseId}/toggle`); loadDay(); }
    catch (e) { toast.error('Oops!'); }
  };

  const redeem = async (rewardId) => {
    try {
      const r = await kidApi().post('/kidmode/redeem', { reward_id: rewardId });
      toast.success(r.data.message);
      loadRewards();
    } catch (e) { toast.error(e.response?.data?.detail || 'Oops!'); }
  };

  const setGoal = async (rewardId) => {
    try {
      await kidApi().post('/kidmode/goal', { reward_id: rewardId });
      toast.success(rewardId ? 'Goal set! Watch your progress bar grow 💪' : 'Goal cleared');
      loadRewards(); loadDay();
    } catch (e) { toast.error('Oops!'); }
  };

  const logout = () => { localStorage.removeItem('kid_token'); setLoggedKid(null); setSelectedKid(null); setDay(null); setView('home'); };

  if (loading) return <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 flex items-center justify-center"><div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin" /></div>;

  // --- Login flow ---
  if (!loggedKid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 flex items-center justify-center p-6">
        <Toaster position="top-center" richColors />
        {selectedKid ? (
          <PinPad kid={selectedKid} onBack={() => setSelectedKid(null)}
            onSuccess={async () => { await loadDay(); }} />
        ) : (
          <div className="text-center" data-testid="kid-profile-picker">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Who's playing today? 🎮</h1>
            <p className="text-white/70 mb-8">Tap your face to enter Kid Mode</p>
            <div className="flex gap-6 flex-wrap justify-center">
              {profiles.map(p => (
                <button key={p.kid_id} onClick={() => p.has_pin ? setSelectedKid(p) : toast.error('Ask mom or dad to set your PIN first!')}
                  data-testid={`kid-profile-${p.kid_id}`} className="flex flex-col items-center gap-2 group">
                  <div className="w-24 h-24 rounded-full flex items-center justify-center text-5xl shadow-xl group-active:scale-95 transition-transform"
                    style={{ backgroundColor: p.avatar_color }}>{p.avatar_emoji}</div>
                  <span className="text-white font-semibold">{p.name}</span>
                  {!p.has_pin && <span className="text-white/50 text-xs">No PIN set</span>}
                </button>
              ))}
              {profiles.length === 0 && <p className="text-white/80">No kid profiles yet — ask a parent to add you in Kids Zone!</p>}
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- Logged in ---
  const pendingTasks = day?.tasks?.filter(t => t.status === 'pending') || [];
  const doneTasks = day?.tasks?.filter(t => t.status !== 'pending') || [];
  const pendingDoses = day?.doses?.filter(d => !d.taken) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 pb-24" data-testid="kid-mode-dashboard">
      <Toaster position="top-center" richColors />
      {celebrate > 0 && <Confetti amount={celebrate} />}
      <div className="max-w-2xl mx-auto p-5 space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3 pt-2">
          <div className="w-14 h-14 rounded-full flex items-center justify-center text-3xl shadow-lg" style={{ backgroundColor: loggedKid.avatar_color }}>{loggedKid.avatar_emoji}</div>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white">Hey {loggedKid.name}! 👋</h1>
            <p className="text-white/70 text-sm">{new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          </div>
          <button onClick={logout} data-testid="kid-logout-btn" className="p-2.5 rounded-full bg-white/15 text-white"><SignOut size={18} /></button>
        </div>

        {/* Points + streak */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white/15 backdrop-blur rounded-2xl p-4 text-center" data-testid="kid-points-card">
            <Star size={28} weight="fill" className="text-yellow-300 mx-auto" />
            <p className="text-3xl font-extrabold text-white mt-1">{day?.kid?.points_balance || 0}</p>
            <p className="text-white/70 text-xs font-semibold uppercase tracking-wide">My Points</p>
          </div>
          <div className="bg-white/15 backdrop-blur rounded-2xl p-4 text-center" data-testid="kid-streak-card">
            <Fire size={28} weight="fill" className="text-orange-300 mx-auto" />
            <p className="text-3xl font-extrabold text-white mt-1">{day?.streak || 0}</p>
            <p className="text-white/70 text-xs font-semibold uppercase tracking-wide">Day Streak</p>
            <p className="text-yellow-200/90 text-[10px] font-bold mt-0.5">7 days = +25 bonus! ⭐</p>
          </div>
        </div>

        {view === 'home' && (
          <>
            <NextUpBanner schedule={day?.schedule} />

            {day?.dabba && (
              <div className="bg-white rounded-2xl p-5 shadow-xl" data-testid="kid-dabba-card">
                <h2 className="font-bold text-gray-800 mb-3">🍱 Today's Dabba</h2>
                <div className="space-y-2">
                  {[['school_tiffin', '🍛', 'School Tiffin'], ['school_snack', '🍎', 'School Snack'], ['bus_snack', '🚌', 'Bus Snack']].map(([key, emoji, label]) => day.dabba[key] && (
                    <div key={key} className="flex items-center gap-3 bg-yellow-50 rounded-xl p-3">
                      <span className="text-2xl">{emoji}</span>
                      <div className="flex-1">
                        <p className="text-[10px] font-bold text-yellow-600 uppercase tracking-wide">{label}</p>
                        <p className="font-semibold text-gray-800 text-sm">{day.dabba[key].replace(/\s*\(new\)\s*$/i, '')}</p>
                      </div>
                      {/\(new\)\s*$/i.test(day.dabba[key]) && <span className="text-[10px] font-bold text-purple-500 bg-purple-100 rounded-full px-2 py-0.5">✨ new!</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {day?.goal && (
              <div className="bg-white rounded-2xl p-4 shadow-xl" data-testid="kid-goal-card">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">{day.goal.emoji}</span>
                  <div className="flex-1">
                    <p className="font-bold text-gray-800 text-sm">My Goal: {day.goal.title}</p>
                    <p className="text-xs text-gray-500">{day.goal.remaining > 0 ? `${day.goal.remaining} points to go!` : 'You did it — ask to redeem! 🎉'}</p>
                  </div>
                  <span className="font-extrabold text-amber-500">{day.goal.pct}%</span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-700" style={{ width: `${day.goal.pct}%` }} />
                </div>
              </div>
            )}

            {(day?.weekend_plan || []).length > 0 && (
              <div className="bg-white rounded-2xl p-5 shadow-xl" data-testid="kid-weekend-card">
                <h2 className="font-bold text-gray-800 mb-3">🎉 Weekend Fun Today!</h2>
                <div className="space-y-2">
                  {day.weekend_plan.map((a, i) => (
                    <div key={i} className="flex items-center gap-3 bg-orange-50 rounded-xl p-3">
                      <span className="text-2xl">{a.emoji}</span>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800 text-sm">{a.title}</p>
                        <p className="text-xs text-gray-500">{a.time}{a.location && a.location.toLowerCase() !== 'home' ? ` · 📍 ${a.location}` : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Today schedule */}
            <div className="bg-white rounded-2xl p-5 shadow-xl">
              <h2 className="font-bold text-gray-800 flex items-center gap-2 mb-3"><Clock size={18} className="text-indigo-500" /> Today's Schedule</h2>
              <div className="space-y-2">
                {(day?.schedule || []).map(s => (
                  <div key={s.entry_id} className="flex items-center gap-3 bg-indigo-50 rounded-xl p-3">
                    <span className="text-2xl">{TYPE_EMOJI[s.type] || '📌'}</span>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800 text-sm">{s.title}</p>
                      <p className="text-xs text-gray-500">{s.start} – {s.end}{s.location && ` · ${s.location}`}</p>
                    </div>
                  </div>
                ))}
                {(!day?.schedule || day.schedule.length === 0) && <p className="text-sm text-gray-400 text-center py-3">No classes today — free day! 🎉</p>}
              </div>
            </div>

            {/* Tasks */}
            <div className="bg-white rounded-2xl p-5 shadow-xl">
              <h2 className="font-bold text-gray-800 flex items-center gap-2 mb-3">✅ My Tasks {pendingTasks.length > 0 && <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">{pendingTasks.length} left</span>}</h2>
              <div className="space-y-2">
                {pendingTasks.map(t => (
                  <div key={t.log_id} className="flex items-center gap-3 bg-amber-50 rounded-xl p-3" data-testid={`kid-task-${t.log_id}`}>
                    <span className="text-2xl">{t.emoji || '⭐'}</span>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800 text-sm">{t.title}</p>
                      <p className="text-xs text-amber-600 font-bold">+{t.points} points</p>
                    </div>
                    <button onClick={() => markDone(t.log_id)} data-testid={`kid-done-${t.log_id}`}
                      className="px-4 py-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-bold shadow active:scale-95 transition-transform">
                      I did it!
                    </button>
                  </div>
                ))}
                {doneTasks.map(t => (
                  <div key={t.log_id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 opacity-70">
                    <span className="text-2xl">{t.emoji || '⭐'}</span>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-500 text-sm line-through">{t.title}</p>
                      <p className="text-xs text-gray-400">{t.status === 'approved' ? `✅ Approved! +${t.points} pts` : t.status === 'done' ? '⏳ Waiting for mom/dad' : t.status === 'missed' ? '😴 Missed this one' : '❌ Not approved'}</p>
                    </div>
                  </div>
                ))}
                {day?.tasks?.length === 0 && <p className="text-sm text-gray-400 text-center py-3">No tasks today!</p>}
              </div>
            </div>

            {/* Medicines */}
            {(day?.doses || []).length > 0 && (
              <div className="bg-white rounded-2xl p-5 shadow-xl">
                <h2 className="font-bold text-gray-800 mb-3">💊 My Medicines {pendingDoses.length === 0 && <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full">All done!</span>}</h2>
                <div className="space-y-2">
                  {day.doses.map(d => (
                    <button key={d.dose_id} onClick={() => toggleDose(d.dose_id)} data-testid={`kid-dose-${d.dose_id}`}
                      className="w-full flex items-center gap-3 bg-blue-50 rounded-xl p-3 text-left active:scale-[0.98] transition-transform">
                      {d.taken ? <CheckCircle size={26} weight="fill" className="text-green-500" /> : <Circle size={26} className="text-gray-300" />}
                      <div className="flex-1">
                        <p className={`font-semibold text-sm ${d.taken ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{d.medicine_name}</p>
                        <p className="text-xs text-gray-500">{d.dosage && `${d.dosage} · `}{d.slot}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {view === 'rewards' && rewardsData && (
          <div className="bg-white rounded-2xl p-5 shadow-xl" data-testid="kid-rewards-view">
            <h2 className="font-bold text-gray-800 mb-1">🎁 Rewards Store</h2>
            <p className="text-xs text-gray-500 mb-4">You have <span className="font-bold text-amber-500">⭐ {rewardsData.balance}</span> points to spend!</p>
            <div className="grid grid-cols-2 gap-3">
              {rewardsData.rewards.map(r => {
                const affordable = rewardsData.balance >= r.cost;
                const alreadyAsked = rewardsData.my_redemptions.some(x => x.reward_id === r.reward_id && x.status === 'requested');
                const isGoal = rewardsData.goal_reward_id === r.reward_id;
                return (
                  <div key={r.reward_id} className={`rounded-xl p-4 border-2 relative ${isGoal ? 'border-purple-400 bg-purple-50' : affordable ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50 opacity-60'}`} data-testid={`kid-reward-${r.reward_id}`}>
                    <button onClick={() => setGoal(isGoal ? '' : r.reward_id)} data-testid={`kid-goal-${r.reward_id}`}
                      title={isGoal ? 'Remove goal' : 'Make this my goal'}
                      className={`absolute top-2 right-2 text-lg ${isGoal ? '' : 'grayscale opacity-40'}`}>🎯</button>
                    <span className="text-3xl">{r.emoji}</span>
                    <p className="font-bold text-gray-800 text-sm mt-1 leading-tight">{r.title}</p>
                    <p className="text-amber-600 font-bold text-sm mt-1">⭐ {r.cost}</p>
                    <button onClick={() => redeem(r.reward_id)} disabled={!affordable || alreadyAsked} data-testid={`kid-redeem-${r.reward_id}`}
                      className="mt-2 w-full py-1.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs font-bold disabled:from-gray-300 disabled:to-gray-300 active:scale-95 transition-transform">
                      {alreadyAsked ? 'Asked! ⏳' : affordable ? 'Get it!' : `Need ${r.cost - rewardsData.balance} more`}
                    </button>
                  </div>
                );
              })}
              {rewardsData.rewards.length === 0 && <p className="col-span-2 text-sm text-gray-400 text-center py-4">No rewards yet — ask mom or dad to add some!</p>}
            </div>
            {rewardsData.my_redemptions.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-100">
                <p className="text-xs font-bold text-gray-500 mb-2">My requests</p>
                {rewardsData.my_redemptions.slice(0, 5).map(r => (
                  <div key={r.redemption_id} className="flex justify-between text-xs py-1">
                    <span className="text-gray-600">{r.reward_emoji} {r.reward_title}</span>
                    <span>{r.status === 'approved' ? '✅' : r.status === 'rejected' ? '❌' : '⏳'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-gray-200 flex justify-around py-2 max-w-2xl mx-auto rounded-t-2xl shadow-2xl">
        <button onClick={() => { setView('home'); loadDay(); }} data-testid="kid-nav-home"
          className={`flex flex-col items-center gap-0.5 px-6 py-1 ${view === 'home' ? 'text-purple-600' : 'text-gray-400'}`}>
          <House size={24} weight={view === 'home' ? 'fill' : 'regular'} /><span className="text-[10px] font-bold">My Day</span>
        </button>
        <button onClick={() => { setView('rewards'); loadRewards(); }} data-testid="kid-nav-rewards"
          className={`flex flex-col items-center gap-0.5 px-6 py-1 ${view === 'rewards' ? 'text-purple-600' : 'text-gray-400'}`}>
          <Gift size={24} weight={view === 'rewards' ? 'fill' : 'regular'} /><span className="text-[10px] font-bold">Rewards</span>
        </button>
      </div>
    </div>
  );
}
